import React, { useMemo } from 'react';
import {
  UPRIGHT_BASS_TUNING,
  TOTAL_POSITIONS_BASS,
  POSITION_MARKERS_BASS,
  BASS_DEGREE_LABELS,
  CHORD_DEGREE_COLORS,
  buildFingerboardMatrix,
  buildChordChangesFingerings,
  resolveBassFormula,
} from '../../lib/upright-bass-theory';
import { useIsDesktop } from '../../hooks/useIsDesktop';

// ── Layout (rebuild plan, open question 9) ────────────────────────────────────
//
// The board is drawn VERTICALLY (nut at the top, strings running down, E A D G left to
// right), like the fiddle. Every number below is a viewBox unit authored at a natural
// scale; the SVG is then rendered at SCALE_PHONE / SCALE_DESKTOP screen px per unit.
// Anything that must stay a fixed screen size (text, rings, string weight) goes through
// px(), which divides by the scale.
//
// Phone 0.78 puts the strings 28px apart (the guitar's density on board 18); dots are
// 13/36 = 72% of the string gap.

const SCALE_PHONE   = 0.78;
const SCALE_DESKTOP = 1.2;

const NOTE_RADIUS    = 13;
const STRING_GAP     = 36;   // horizontal distance between strings
const POSITION_GAP   = 42;   // vertical distance between positions
const TOP_PAD        = 74;   // above the nut: string names, open-string dots and their Roman numerals
const BOTTOM_PAD     = 24;
const BOARD_X        = 2;    // left edge of the board
const BOARD_PAD      = 28;   // board edge to the outer string centre
const BOARD_OVERRUN  = 14;   // the board runs a little past the octave
const MARKER_INSET   = 21;   // side marker dots sit this far right of the last string
const ROMAN_GAP      = 6;    // Roman numerals start this far right of the board edge

const NUM_STRINGS  = 4;
const STRING_NAMES = ['E', 'A', 'D', 'G']; // left = lowest
// Screen-px string weights: thicker for the lower strings
const STRING_WEIGHT = [2.4, 2, 1.6, 1.2];

// ── Geometry helpers ──────────────────────────────────────────────────────────

const stringX   = (s) => BOARD_X + BOARD_PAD + s * STRING_GAP;
const positionY = (p) => TOP_PAD + p * POSITION_GAP;

const BOARD_RIGHT  = stringX(NUM_STRINGS - 1) + BOARD_PAD;
const BOARD_BOTTOM = positionY(TOTAL_POSITIONS_BASS) + BOARD_OVERRUN;
const MARKER_X     = stringX(NUM_STRINGS - 1) + MARKER_INSET;
const ROMAN_X      = BOARD_RIGHT + ROMAN_GAP;
const VIEW_W       = ROMAN_X + 24;
const VIEW_H       = positionY(TOTAL_POSITIONS_BASS) + BOTTOM_PAD;

// Y for open-string dots (above the nut) and string name labels (above those)
const OPEN_Y        = TOP_PAD - 34;
const STRING_NAME_Y = TOP_PAD - 62;

const ROMAN_LABELS  = { 3: 'III', 5: 'V', 7: 'VII', 10: 'X', 12: 'XII' };
const DEGREE_ROMANS = { 1: 'I', 2: 'ii', 3: 'iii', 4: 'IV', 5: 'V', 6: 'vi', 7: 'vii°' };

// ── Chord Changes helpers ─────────────────────────────────────────────────────

/**
 * Build a map of "string-position" → array of { degree, isRoot } for chord-
 * changes mode. Used to detect shared notes and render the overlap pattern.
 */
function buildChordNoteMap(fingerings) {
  const map = new Map();
  fingerings.forEach(({ degree, rootNode, fifthNode }) => {
    [rootNode, fifthNode].forEach(node => {
      const key = `${node.string}-${node.position}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ degree, isRoot: node.isRoot, noteName: node.noteName });
    });
  });
  return map;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function UprightBassFingerboard({
  musicalKey,
  tonality,
  structure,
  stringFocus,
  filter,
  label,
  chordChangesActive,
  selectedDegrees,
}) {
  const isDesktop = useIsDesktop();
  const k  = isDesktop ? SCALE_DESKTOP : SCALE_PHONE;
  /** A screen-pixel size expressed in view units. */
  const px = (v) => v / k;
  const RING        = px(1.25);
  const NOTE_FONT   = isDesktop ? 12 : 9;
  const NOTE_FONT_S = isDesktop ? 10 : 8;

  const formula = useMemo(
    () => resolveBassFormula(structure, tonality),
    [structure, tonality]
  );

  // Build the set of visible note nodes depending on mode
  const { normalNodes, chordNodes, chordNoteMap } = useMemo(() => {
    if (structure === 'Root & 5th' && chordChangesActive) {
      const fingerings   = buildChordChangesFingerings(musicalKey, selectedDegrees);
      const chordNoteMap = buildChordNoteMap(fingerings);
      // Flatten into a simple list for rendering, keeping degree info
      const chordNodes = [];
      fingerings.forEach(({ degree, rootNode, fifthNode }) => {
        chordNodes.push({ ...rootNode,  degree });
        chordNodes.push({ ...fifthNode, degree });
      });
      return { normalNodes: [], chordNodes, chordNoteMap };
    }

    const matrix = buildFingerboardMatrix(musicalKey, formula, UPRIGHT_BASS_TUNING, tonality);
    const rawNodes = [];
    matrix.forEach((stringPositions, sIdx) => {
      if (!stringFocus[sIdx]) return;
      stringPositions.forEach(note => {
        if (!note.isInScale) return;
        rawNodes.push({
          string:   sIdx,
          position: note.positionIndex,
          isRoot:   note.isRoot,
          interval: note.interval,
          noteName: note.noteName,
        });
      });
    });

    // Apply filter
    let visible = rawNodes;
    if (filter === 'Roots Only')  visible = rawNodes.filter(n => n.isRoot);
    if (filter === 'Triads Only') visible = rawNodes.filter(n => [0, 3, 4, 7].includes(n.interval));

    return { normalNodes: visible, chordNodes: [], chordNoteMap: new Map() };
  }, [musicalKey, tonality, formula, structure, stringFocus, filter, chordChangesActive, selectedDegrees]);

  // Label text for a normal note
  const noteLabel = (node) => {
    if (label === 'Note Names') return node.noteName;
    if (label === 'Intervals')  return BASS_DEGREE_LABELS[node.interval] ?? '';
    return '';
  };

  // Strings whose open position has a dot drawn; the rest get a faint outline ring above the
  // nut so the open-string slot is still visible.
  const openDrawn = useMemo(() => {
    const drawn = new Set();
    normalNodes.forEach(n => { if (n.position === 0) drawn.add(n.string); });
    chordNodes.forEach(n => { if (n.position === 0) drawn.add(n.string); });
    return drawn;
  }, [normalNodes, chordNodes]);

  const mutedText = { fill: 'hsl(var(--ink-muted))' };
  const noteY = (position) => (position === 0 ? OPEN_Y : positionY(position));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        style={{ width: VIEW_W * k, maxWidth: '100%', height: 'auto', display: 'block', margin: '0 auto' }}
        className="select-none"
      >
        {/* ── 1. Board surface — paper, hairline edges, no fret wires ───── */}
        <rect
          x={BOARD_X} y={TOP_PAD} width={BOARD_RIGHT - BOARD_X} height={BOARD_BOTTOM - TOP_PAD}
          style={{ fill: 'hsl(var(--board))' }}
        />
        <rect x={BOARD_X} y={TOP_PAD} width={px(1)} height={BOARD_BOTTOM - TOP_PAD} style={{ fill: 'hsl(var(--board-edge))' }} />
        <rect x={BOARD_RIGHT - px(1)} y={TOP_PAD} width={px(1)} height={BOARD_BOTTOM - TOP_PAD} style={{ fill: 'hsl(var(--board-edge))' }} />
        <rect x={BOARD_X} y={BOARD_BOTTOM - px(1)} width={BOARD_RIGHT - BOARD_X} height={px(1)} style={{ fill: 'hsl(var(--board-edge))' }} />

        {/* ── 2. Side-marker dots (tape marks), pale like the guitar inlays ─ */}
        {POSITION_MARKERS_BASS.map(p => {
          const cy = positionY(p);
          const dot = { fill: 'hsl(var(--inlay))' };
          if (p === 12) {
            // Double dot at octave position
            return (
              <g key={`marker-${p}`}>
                <circle cx={MARKER_X} cy={cy - 6} r={4} style={dot} />
                <circle cx={MARKER_X} cy={cy + 6} r={4} style={dot} />
              </g>
            );
          }
          return <circle key={`marker-${p}`} cx={MARKER_X} cy={cy} r={4} style={dot} />;
        })}

        {/* ── 3. Position Roman numerals ──────────────────────────────────── */}
        {POSITION_MARKERS_BASS.map(p => (
          <text
            key={`roman-${p}`}
            x={ROMAN_X}
            y={positionY(p)}
            dominantBaseline="central"
            style={mutedText}
            fontSize={px(10)}
            fontWeight={700}
          >
            {ROMAN_LABELS[p]}
          </text>
        ))}

        {/* ── 4. Nut bar — solid ink ──────────────────────────────────────── */}
        <rect
          x={BOARD_X}
          y={TOP_PAD - 2.5}
          width={BOARD_RIGHT - BOARD_X}
          height={5}
          style={{ fill: 'hsl(var(--string))' }}
        />

        {/* ── 5. Strings (vertical lines) — solid ink; muted strings fade ─── */}
        {UPRIGHT_BASS_TUNING.map((_, s) => (
          <line
            key={`string-${s}`}
            x1={stringX(s)}
            y1={TOP_PAD}
            x2={stringX(s)}
            y2={BOARD_BOTTOM}
            style={{ stroke: 'hsl(var(--string))' }}
            strokeWidth={px(STRING_WEIGHT[s])}
            opacity={stringFocus[s] ? 1 : 0.2}
          />
        ))}

        {/* ── 6. String name labels ────────────────────────────────────────── */}
        {STRING_NAMES.map((name, s) => (
          <text
            key={`name-${s}`}
            x={stringX(s)}
            y={STRING_NAME_Y}
            textAnchor="middle"
            dominantBaseline="central"
            style={mutedText}
            fontSize={px(isDesktop ? 14 : 12)}
            fontWeight={700}
            opacity={stringFocus[s] ? 1 : 0.2}
          >
            {name}
          </text>
        ))}

        {/* ── 7. Empty open-string slots: a faint ring where no dot is drawn ── */}
        {UPRIGHT_BASS_TUNING.map((_, s) => {
          if (openDrawn.has(s)) return null;
          return (
            <circle
              key={`open-${s}`}
              cx={stringX(s)}
              cy={OPEN_Y}
              r={NOTE_RADIUS - px(0.5)}
              fill="none"
              style={{ stroke: 'hsl(var(--ink-muted))' }}
              strokeWidth={px(1)}
              opacity={stringFocus[s] ? 0.3 : 0.15}
            />
          );
        })}

        {/* ── 8a. Normal notes (non-chord-changes mode), incl. open strings ── */}
        {normalNodes.map(node => {
          const cx = stringX(node.string);
          const cy = noteY(node.position);
          const lbl = noteLabel(node);
          return (
            <g
              key={`note-${node.string}-${node.position}`}
              className="animate-note-spring"
              style={{ animationDelay: `${node.position * 12}ms` }}
            >
              <circle
                cx={cx} cy={cy} r={NOTE_RADIUS - RING / 2}
                strokeWidth={RING}
                style={{
                  fill:   node.isRoot ? 'hsl(var(--root))' : 'hsl(var(--note-scale-fill))',
                  stroke: node.isRoot ? 'hsl(var(--root))' : 'hsl(var(--sage))',
                }}
              />
              {lbl && (
                <text
                  x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
                  fontSize={px(lbl.length > 2 ? NOTE_FONT_S : NOTE_FONT)} fontWeight={800}
                  className="pointer-events-none"
                  style={{ fill: node.isRoot ? 'hsl(var(--primary-foreground))' : 'hsl(var(--ink))' }}
                >
                  {lbl}
                </text>
              )}
            </g>
          );
        })}

        {/* ── 8b. Chord Changes notes: filled in the degree's colour ──────── */}
        {chordChangesActive && (() => {
          // Deduplicate by string-position, rendering each unique location once
          const rendered = new Set();
          return chordNodes.map(node => {
            const mapKey = `${node.string}-${node.position}`;
            if (rendered.has(mapKey)) return null;
            rendered.add(mapKey);

            const entries = chordNoteMap.get(mapKey) || [];
            // Sort by degree so lowest degree = primary fill
            const sorted  = [...entries].sort((a, b) => a.degree - b.degree);
            const primary  = sorted[0];
            const secondary = sorted[1];

            const cx = stringX(node.string);
            const cy = noteY(node.position);
            const primaryColor = CHORD_DEGREE_COLORS[primary.degree];
            const secondaryColor = secondary ? CHORD_DEGREE_COLORS[secondary.degree] : null;

            // Label text: note name, or the interval when Labels is on Intervals
            const lbl = label === 'Intervals'
              ? (BASS_DEGREE_LABELS[node.interval] ?? '')
              : primary.noteName;

            // Roman numerals of every chord that uses this note, e.g. "I,IV"
            const romanLabel = sorted.map(e => DEGREE_ROMANS[e.degree]).join(',');

            return (
              <g key={`chord-${mapKey}`}
                className="animate-note-spring"
                style={{ animationDelay: `${node.position * 12}ms` }}
              >
                {/* Outer ring for the secondary chord colour (a shared note) */}
                {secondaryColor && (
                  <circle cx={cx} cy={cy} r={NOTE_RADIUS + px(2.5)}
                    style={{ fill: `hsl(var(${secondaryColor.fillVar}))` }}
                  />
                )}
                <circle cx={cx} cy={cy} r={NOTE_RADIUS}
                  style={{ fill: `hsl(var(${primaryColor.fillVar}))` }}
                  strokeWidth={0}
                />
                {lbl && (
                  <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
                    fontSize={px(lbl.length > 2 ? NOTE_FONT_S : NOTE_FONT)} fontWeight={800}
                    style={{ fill: 'hsl(var(--chord-label))' }}
                    className="pointer-events-none"
                  >
                    {lbl}
                  </text>
                )}
                {/* The Roman numeral sits under the dot so it never collides with the
                    dot on the neighbouring string */}
                <text
                  x={cx}
                  y={cy + NOTE_RADIUS + px(7)}
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={mutedText}
                  fontSize={px(9)}
                  fontWeight={800}
                  className="pointer-events-none"
                >
                  {romanLabel}
                </text>
              </g>
            );
          });
        })()}
      </svg>
    </div>
  );
}
